import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Workarea from "./Workarea";
import TrainerEditor from "./trainer/TrainerEditor";
import KnowledgeBaseEditor from "./knowledgebase/KnowledgeBaseEditor";
import { getGame } from "../redux/asyncThunk/userActions";
import CircularProgress from "@mui/material/CircularProgress";
import { userSlice } from "../redux/user/userSlice";
import { buildInitialSceneNodes } from "./nodes/FirstSceneNode";
import {
  CONTENT_TYPES,
  isTrainerGame,
  isKnowledgeBaseGame,
  resolveGameContentType,
} from "../utils/contentType";
import {
  isTrainerProjectHint,
  markTrainerProjectHint,
} from "../utils/trainerProjectHint";
import {
  isKbProjectHint,
  markKbProjectHint,
} from "../utils/kbProjectHint";

const { addNode, selectProject } = userSlice;

export default function Game() {
  const [gameData, setGameData] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { id: routeGameId } = useParams();
  const [searchParams] = useSearchParams();
  const selectedGameIdFromStore = useSelector((state) => state.user?.selectedGameId);
  const selectedGameId = selectedGameIdFromStore || routeGameId;
  const status = useSelector((state) => state.user?.status);
  const game = useSelector((state) => state.user.game);

  const trainerHint =
    searchParams.get("type") === CONTENT_TYPES.trainer ||
    isTrainerProjectHint(selectedGameId);

  const kbHint =
    searchParams.get("type") === CONTENT_TYPES.knowledge_base ||
    isKbProjectHint(selectedGameId);

  const treatAsTrainer = useCallback(
    (gameDoc) => isTrainerGame(gameDoc, { trainerHint, kbHint }),
    [trainerHint, kbHint]
  );

  const treatAsKnowledgeBase = useCallback(
    (gameDoc) => isKnowledgeBaseGame(gameDoc, { kbHint, trainerHint }),
    [kbHint, trainerHint]
  );

  const normalizeTrainerGame = (gameDoc) => ({
    ...gameDoc,
    contentType: CONTENT_TYPES.trainer,
    settings: {
      ...(gameDoc.settings ?? {}),
      contentType: CONTENT_TYPES.trainer,
    },
  });

  const normalizeKnowledgeBaseGame = (gameDoc) => ({
    ...gameDoc,
    contentType: CONTENT_TYPES.knowledge_base,
    settings: {
      ...(gameDoc.settings ?? {}),
      contentType: CONTENT_TYPES.knowledge_base,
    },
  });

  useEffect(() => {
    if (routeGameId && routeGameId !== selectedGameIdFromStore) {
      dispatch(selectProject(routeGameId));
    }
  }, [dispatch, routeGameId, selectedGameIdFromStore]);

  useEffect(() => {
    if (selectedGameId) {
      setGameData(null);
      dispatch(getGame({ gameId: selectedGameId }));
    } else {
      navigate("/main", { replace: true });
    }
  }, [dispatch, navigate, selectedGameId]);

  useEffect(() => {
    if (!game || String(game._id) !== String(selectedGameId)) return;

    if (treatAsKnowledgeBase(game)) {
      if (isKnowledgeBaseGame(game)) {
        markKbProjectHint(selectedGameId);
      }
      setGameData(normalizeKnowledgeBaseGame(game));
      return;
    }

    if (treatAsTrainer(game)) {
      if (isTrainerGame(game)) {
        markTrainerProjectHint(selectedGameId);
      }
      setGameData(normalizeTrainerGame(game));
      return;
    }

    if (status === "loading") return;

    if (
      resolveGameContentType(game) === CONTENT_TYPES.scenario &&
      (game.nodes?.length ?? 0) < 1
    ) {
      dispatch(addNode({ data: [...buildInitialSceneNodes(false)] }));
      return;
    }

    setGameData(game);
  }, [
    dispatch,
    game,
    selectedGameId,
    status,
    trainerHint,
    kbHint,
    treatAsTrainer,
    treatAsKnowledgeBase,
  ]);

  const isInitialLoading = status === "loading" && !gameData;
  const isTrainer = treatAsTrainer(gameData);
  const isKnowledgeBase = treatAsKnowledgeBase(gameData);
  const isAlternateEditor = isTrainer || isKnowledgeBase;

  return (
    <div
      className={`app-editor${
        isTrainer
          ? " app-editor--trainer"
          : isKnowledgeBase
            ? " app-editor--knowledge-base"
            : ""
      }`}
    >
      {!isAlternateEditor ? (
        <header className="app-editor__header">
          <h1 className="app-editor__title">
            {gameData?.title || "Загрузка проекта…"}
          </h1>
        </header>
      ) : null}

      {isInitialLoading ? (
        <div className="app-loading">
          <CircularProgress />
        </div>
      ) : gameData && isKnowledgeBase ? (
        <KnowledgeBaseEditor game={gameData} />
      ) : gameData && isTrainer ? (
        <TrainerEditor game={gameData} />
      ) : gameData ? (
        <Workarea game={gameData} />
      ) : null}
    </div>
  );
}
